import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

const AcademicProjects = () => {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTerm, setFilterTerm] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const [newProject, setNewProject] = useState({
    projectName: '',
    projectType: 'capstone',
    academicInfo: {
      institution: '',
      department: '',
      advisor: '',
      gradYear: '',
      term: ''
    },
    logline: '',
    synopsis: '',
    genre: 'Drama',
    phase: 'Development',
    status: 'active',
  });

  const projectTypes = [
    { value: 'capstone', label: 'Capstone Project' },
    { value: 'thesis', label: 'Thesis Film' },
    { value: 'student', label: 'Student Project' }
  ];

  const academicTerms = ['Fall', 'Winter', 'Spring', 'Summer'];
  const departments = ['Film & Television', 'Animation', 'Visual Effects', 'Sound Design', 'Other'];
  const genres = ['Drama', 'Comedy', 'Documentary', 'Animation', 'Experimental', 'Other'];

  useEffect(() => {
    fetchAcademicProjects();
  }, []);

  useEffect(() => {
    filterAndSortProjects();
  }, [projects, searchTerm, filterTerm, sortBy]);

  const fetchAcademicProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/projects`);
      // Only get academic projects
      const academicProjects = response.data.filter(project => 
        ['capstone', 'thesis', 'student'].includes(project.projectType)
      );
      setProjects(academicProjects);
      setFilteredProjects(academicProjects);
    } catch (error) {
      setErrors(['Failed to fetch projects']);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProjects = () => {
    let filtered = [...projects];

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.academicInfo?.institution?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.academicInfo?.advisor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.academicInfo?.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterTerm !== 'all') {
      filtered = filtered.filter(project => project.projectType === filterTerm);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.projectName.localeCompare(b.projectName);
        case 'type':
          return a.projectType.localeCompare(b.projectType);
        case 'institution':
          return (a.academicInfo?.institution || '').localeCompare(b.academicInfo?.institution || '');
        case 'date':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    setFilteredProjects(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/v1/projects`, newProject);
      setOpen(false);
      fetchAcademicProjects();
      setNewProject({
        projectName: '',
        projectType: 'capstone',
        academicInfo: {
          institution: '',
          department: '',
          advisor: '',
          gradYear: '',
          term: ''
        },
        logline: '',
        synopsis: '',
        genre: 'Drama',
        phase: 'Development',
        status: 'active',
      });
    } catch (error) {
      setErrors(['Failed to create project']);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Academic Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          New Academic Project
        </Button>
      </Box>

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Project Type</InputLabel>
            <Select
              value={filterTerm}
              onChange={(e) => setFilterTerm(e.target.value)}
              label="Project Type"
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Types</MenuItem>
              {projectTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort by"
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon />
                </InputAdornment>
              }
            >
              <MenuItem value="date">Date Created</MenuItem>
              <MenuItem value="name">Project Name</MenuItem>
              <MenuItem value="type">Project Type</MenuItem>
              <MenuItem value="institution">Institution</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Project Cards */}
      <Grid container spacing={3}>
        {filteredProjects.map((project) => (
          <Grid item xs={12} sm={6} md={4} key={project._id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            >
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom noWrap>
                  {project.projectName}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    size="small"
                    icon={<SchoolIcon />}
                    label={projectTypes.find(t => t.value === project.projectType)?.label}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={project.academicInfo?.institution}
                    color="secondary"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {project.logline}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title="Department">
                    <Chip
                      size="small"
                      label={project.academicInfo?.department}
                      variant="outlined"
                    />
                  </Tooltip>
                  <Tooltip title="Academic Term">
                    <Chip
                      size="small"
                      label={`${project.academicInfo?.term} ${project.academicInfo?.gradYear}`}
                      variant="outlined"
                    />
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create Academic Project Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Academic Project</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Name"
                required
                value={newProject.projectName}
                onChange={(e) => setNewProject({ ...newProject, projectName: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={newProject.projectType}
                  onChange={(e) => setNewProject({ ...newProject, projectType: e.target.value })}
                  label="Project Type"
                >
                  {projectTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  value={newProject.academicInfo.department}
                  onChange={(e) => setNewProject({
                    ...newProject,
                    academicInfo: { ...newProject.academicInfo, department: e.target.value }
                  })}
                  label="Department"
                >
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Institution"
                value={newProject.academicInfo.institution}
                onChange={(e) => setNewProject({
                  ...newProject,
                  academicInfo: { ...newProject.academicInfo, institution: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Advisor"
                value={newProject.academicInfo.advisor}
                onChange={(e) => setNewProject({
                  ...newProject,
                  academicInfo: { ...newProject.academicInfo, advisor: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Graduation Year"
                value={newProject.academicInfo.gradYear}
                onChange={(e) => setNewProject({
                  ...newProject,
                  academicInfo: { ...newProject.academicInfo, gradYear: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Academic Term</InputLabel>
                <Select
                  value={newProject.academicInfo.term}
                  onChange={(e) => setNewProject({
                    ...newProject,
                    academicInfo: { ...newProject.academicInfo, term: e.target.value }
                  })}
                  label="Academic Term"
                >
                  {academicTerms.map(term => (
                    <MenuItem key={term} value={term}>{term}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Logline"
                value={newProject.logline}
                onChange={(e) => setNewProject({ ...newProject, logline: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Synopsis"
                value={newProject.synopsis}
                onChange={(e) => setNewProject({ ...newProject, synopsis: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Create Project
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AcademicProjects;
